import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { technologyApi } from '../../../api/production/technologies';
import { ConfigurationProfileUpsertForm } from '../../../components/forms/production/config-profile/upsert-form';
import { Skeleton } from '../../../components/ui/skeleton';
import type {
  ConfigProfileFormDefaultValues,
  ITechnology,
} from '../../../hooks/production/use-config-profile-form';
import type { IConfigurationProfile } from '../../../types/production/configuration-profile';

interface CopyFromState {
  copyFrom: IConfigurationProfile;
}

function buildCopyDefaultValues(
  source: IConfigurationProfile,
  technologies: ITechnology[],
): ConfigProfileFormDefaultValues {
  const technology = technologies.find((t) => t.id === source.technology_id);
  const client =
    source.client ??
    (source.client_id
      ? {
          id: source.client_id,
          company_name: source.manual_client ?? source.client_id,
        }
      : undefined);

  return {
    configurationProfile: source,
    technology,
    client,
  };
}

export function ConfigurationProfileCreatePage() {
  const location = useLocation();
  const state = location.state as CopyFromState | null;
  const copyFrom = state?.copyFrom;

  const { data: technologiesData, isLoading: isLoadingTechnologies } = useQuery({
    queryKey: ['technologies'],
    queryFn: () => technologyApi.findMany({}),
  });

  const isLoading = isLoadingTechnologies;
  const technologies = technologiesData?.docs || [];

  const defaultValues = useMemo<ConfigProfileFormDefaultValues | undefined>(() => {
    if (!copyFrom) return undefined;
    return buildCopyDefaultValues(copyFrom, technologies);
  }, [copyFrom, technologies]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="mx-auto max-w-4xl space-y-8">
          <div className="space-y-2">
            <Skeleton className="h-9 w-96" />
            <Skeleton className="h-5 w-64" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Produção &gt; Perfis de Configuração &gt; Novo Perfil de Configuração
          </p>
          <h1 className="text-3xl font-bold tracking-tight">
            {copyFrom ? 'Copiar Perfil de Configuração' : 'Novo Perfil de Configuração'}
          </h1>
          <p className="text-muted-foreground">
            {copyFrom
              ? 'Configure um novo perfil baseado nos parâmetros do perfil copiado'
              : 'Configure um novo perfil para equipamentos IoT'}
          </p>
        </div>
        <ConfigurationProfileUpsertForm
          technologies={technologies}
          defaultValues={defaultValues}
          isCopy={!!copyFrom}
        />
      </div>
    </div>
  );
}

export default ConfigurationProfileCreatePage;
